import { FormErrorWithDiscriminatedField } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/formSchema";
import {
  OperationalTextContextType,
  useOperationalTextContexts,
} from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { useCallback, useEffect, useState } from "react";
import {
  FieldPath,
  FieldValue,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FailureMessageWithRetryButton } from "shared/components/feedback/FailureMessageWithRetryButton";
import {
  FilterChip,
  Field,
  Skeleton,
  Stack,
  Text,
  VStack,
} from "shared/components/Spor";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { getDropsContextType, isDropsContextType } from "shared/types/types";
import { filterAndSortContexts } from "shared/utils/contextUtils";
import { EndsWith } from "shared/utils/objectUtils";

type DistributionsProps<T extends FieldValues> = {
  contextTypesField: FieldPath<T> & EndsWith<"contextTypes">;
  fieldErrors: FormErrorWithDiscriminatedField<"severity" | "distributions">;
};

// T is used here in order to typecheck contextTypesField. Checking whether it is a path of T.
export const Distributions = <T extends FieldValues>({
  contextTypesField,
  fieldErrors,
}: DistributionsProps<T>) => {
  const { t } = useTranslation();
  const {
    data: allContexts,
    isFetching,
    isPending,
    isSuccess,
    isError,
    refetch,
  } = useOperationalTextContexts();

  const [contexts, setContexts] = useState(allContexts ?? []);

  // The original `loading` from tanstack query is only true on first request, not on retries
  const loading = isFetching || isPending;

  const formMethods = useFormContext<T>();
  const contextTypes = formMethods.watch(contextTypesField);

  const distributionErrors: FormErrorWithDiscriminatedField<
    "contextTypes" | "texts"
  > = fieldErrors?.distributions;

  // Add context to selected list if missing, remove from list if present
  const toggleContextSelected = useCallback(
    (context: OperationalTextContextType["type"]) => {
      const currentValues: string[] = contextTypes ?? [];
      if (!currentValues.includes(context)) {
        formMethods.setValue(contextTypesField, [
          ...currentValues,
          context,
        ] as FieldValue<T>);
      } else {
        formMethods.setValue(
          contextTypesField,
          (currentValues.filter((v) => v !== context) ?? []) as FieldValue<T>,
        );
      }
    },
    [formMethods.setValue, contextTypes],
  );

  const countryCode = useCountryCodeForDropsRole();

  useEffect(() => {
    const toggledContexts = contextTypes ?? [getDropsContextType(countryCode)];

    formMethods.setValue(contextTypesField, toggledContexts as FieldValue<T>, {
      shouldValidate: true,
    });
  }, []);

  useEffect(() => {
    setContexts(filterAndSortContexts(allContexts));
  }, [allContexts]);

  return (
    <VStack alignItems="start" gap="0">
      <Field invalid={!!distributionErrors?.contextTypes}>
        <Field as="legend">{t("Mottakere")}</Field>

        {loading && (
          <Stack flexDirection="row" width="100%">
            <Skeleton height="36px" borderRadius={300} flexGrow={2} />
            <Skeleton height="36px" borderRadius={300} flexGrow={3} />
            <Skeleton height="36px" borderRadius={300} flexGrow={1} />
          </Stack>
        )}

        {isError && !loading && (
          <FailureMessageWithRetryButton
            customMessage={t(
              "Kunne ikke hente mottakere fra Operational Text-tjenesten",
            )}
            retryFunction={() => refetch()}
          />
        )}
      </Field>
      {isSuccess && !loading && (
        <Stack flexDirection="row" flexWrap="wrap">
          {contexts?.map((context) => (
            <FilterChip
              disabled={isDropsContextType(context.type)}
              key={context.type}
              onCheckedChange={() => toggleContextSelected(context.type)}
              checked={contextTypes?.includes(context.type)}
            >
              <Text variant="sm" fontWeight="400">
                {context.name}
              </Text>
            </FilterChip>
          ))}
        </Stack>
      )}
    </VStack>
  );
};
